import { z } from 'zod';
/**
 * Neutral server-host capability for optional, tenant-scoped module actions.
 * The consumer and provider validate the versioned domain request/response;
 * this host capability keeps either optional package out of the other's code.
 */
export const platformOptionalActionProviderHostCapability = 'platform.optional-action-provider-host.v1';
/**
 * Receipt-bound approved actions are a breaking evolution of the original
 * optional action host. Keep v1 available for existing providers and use v2
 * only for providers whose owner SQL consumes a Platform invocation receipt.
 */
export const platformReceiptBoundActionProviderHostCapability = 'platform.optional-action-provider-host.v2';
export const platformApprovedActionInvocationReceiptCapability = 'platform.approved-action-invocation-receipts.v1';
export const platformApprovedActionProviderSlot = 'platform.server.approved-actions';
const uuid = z.string().uuid();
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const databaseRevision = z.number().int().positive().max(2_147_483_647);
const identifier = z.string().min(3).max(160)
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/);
const versionedIdentifier = z.string().min(5).max(160)
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
/**
 * Versioned, action-owner-declared Assistant safety metadata
 * (`platform.assistant-action-policy.v1`). The field is OPTIONAL on the
 * generic approved-action descriptor: ordinary non-Assistant Platform action
 * consumers may omit or ignore it, while Assistant proposal and execution
 * paths REQUIRE and validate it for every Assistant-eligible action. An
 * unknown policy version, an effect outside the enumerated safe classes, or
 * any external effect beyond `none`/`draft-only` fails schema parsing —
 * irreversible actions, sends, payments, provider mutations and public
 * publishing are unrepresentable, not merely rejected.
 */
export const platformAssistantActionPolicyVersion = 'platform.assistant-action-policy.v1';
export const platformAssistantActionPolicySchema = z.strictObject({
    policyContractVersion: z.literal(platformAssistantActionPolicyVersion),
    /** Every Assistant write initially requires an explicit human confirmation. */
    confirmation: z.literal('explicit'),
    effectClass: z.enum([
        'reversible-write', 'compensating-write', 'superseding-write', 'draft-only',
    ]),
    externalEffectClass: z.enum(['none', 'draft-only']),
    /**
     * The owner's TRUTHFUL reversal classification. A classification is not an
     * implemented reversal: `registeredReversalActionId` names a registered
     * owner reversal action ONLY when one actually exists; null means no
     * reversal action is implemented yet and the action must stay out of any
     * promoted autonomous catalog.
     */
    reversal: z.strictObject({
        kind: z.enum([
            'inverse-action', 'compensating-action', 'superseding-write', 'draft-discard',
        ]),
        registeredReversalActionId: versionedIdentifier.nullable(),
    }),
    /** Owner readback (execution evidence) is mandatory after every success. */
    ownerReadbackRequired: z.literal(true),
    /** Must equal the owning action's exact `inputSchemaHash`. */
    inputSchemaHash: hash,
});
export const platformApprovedActionDescriptorSchema = z.strictObject({
    actionId: versionedIdentifier,
    displayName: z.string().trim().min(1).max(160),
    targetResourceType: identifier,
    inputSchemaHash: hash,
    requiredPermission: identifier,
    assistantPolicy: platformAssistantActionPolicySchema.optional(),
}).superRefine((action, context) => {
    if (action.assistantPolicy !== undefined &&
        action.assistantPolicy.inputSchemaHash !== action.inputSchemaHash) {
        context.addIssue({
            code: 'custom', path: ['assistantPolicy', 'inputSchemaHash'],
            message: 'The Assistant policy must bind to the exact action input schema hash.',
        });
    }
});
const platformApprovedActionProviderDescriptorFields = {
    kind: z.literal('descriptor'),
    providerId: identifier,
    providerCapability: versionedIdentifier,
    displayName: z.string().trim().min(1).max(160),
    actions: z.array(platformApprovedActionDescriptorSchema).min(1).max(100),
};
export const platformApprovedActionProviderDescriptorV1Schema = z.strictObject({
    ...platformApprovedActionProviderDescriptorFields,
    contractVersion: z.literal(platformOptionalActionProviderHostCapability),
});
export const platformApprovedActionProviderDescriptorV2Schema = z.strictObject({
    ...platformApprovedActionProviderDescriptorFields,
    contractVersion: z.literal(platformReceiptBoundActionProviderHostCapability),
});
export const platformApprovedActionProviderDescriptorSchema = z.union([
    platformApprovedActionProviderDescriptorV1Schema,
    platformApprovedActionProviderDescriptorV2Schema,
]);
const platformApprovedActionInputSchema = z.record(z.string().min(1).max(120), z.unknown()).superRefine((input, context) => {
    try {
        const encoded = JSON.stringify(input);
        if (encoded === undefined || encoded.length > 32_000) {
            context.addIssue({
                code: 'custom',
                message: 'Approved action input must encode to at most 32000 JSON characters.',
            });
        }
    }
    catch {
        context.addIssue({ code: 'custom', message: 'Approved action input must be JSON serializable.' });
    }
});
export const platformApprovedActionProviderRequestV1Schema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('describe'),
        contractVersion: z.literal(platformOptionalActionProviderHostCapability),
    }),
    z.strictObject({
        kind: z.literal('execute'),
        contractVersion: z.literal(platformOptionalActionProviderHostCapability),
        executionId: uuid,
        proposalId: uuid,
        commandId: uuid,
        actionId: versionedIdentifier,
        targetResourceType: identifier,
        targetResourceId: uuid.nullable(),
        input: platformApprovedActionInputSchema,
        inputHash: hash,
        inputSchemaHash: hash,
        requestedByMembershipId: uuid,
        approvedByMembershipId: uuid,
        approvedAt: z.string().datetime({ offset: true }),
    }),
]);
export const platformApprovedActionProviderRequestV2Schema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('describe'),
        contractVersion: z.literal(platformReceiptBoundActionProviderHostCapability),
    }),
    z.strictObject({
        kind: z.literal('execute'),
        contractVersion: z.literal(platformReceiptBoundActionProviderHostCapability),
        invocationReceiptId: uuid,
        executionId: uuid,
        executionRevision: databaseRevision,
        proposalId: uuid,
        proposalRevision: databaseRevision,
        commandId: uuid,
        actionId: versionedIdentifier,
        targetResourceType: identifier,
        targetResourceId: uuid.nullable(),
        input: platformApprovedActionInputSchema,
        inputHash: hash,
        inputSchemaHash: hash,
        requestedByMembershipId: uuid,
        approvedByMembershipId: uuid,
        approvedAt: z.string().datetime({ offset: true }),
    }),
]);
/** Backward-compatible default for existing v1 provider packages. */
export const platformApprovedActionProviderRequestSchema = platformApprovedActionProviderRequestV1Schema;
export const platformApprovedActionProviderAnyRequestSchema = z.union([
    platformApprovedActionProviderRequestV1Schema,
    platformApprovedActionProviderRequestV2Schema,
]);
const platformApprovedActionEvidenceValueSchema = z.union([
    z.string().max(500), z.number().finite(), z.boolean(), z.null(),
]);
export const platformApprovedActionEvidenceSchema = z.strictObject({
    subjectType: identifier,
    subjectId: uuid,
    revision: z.number().int().positive().nullable(),
    summary: z.string().trim().min(1).max(500),
    metadata: z.record(z.string().min(1).max(80), platformApprovedActionEvidenceValueSchema).refine((metadata) => Object.keys(metadata).length <= 25, 'Action evidence has too many fields.'),
});
const platformApprovedActionProviderResultFields = {
    kind: z.literal('execution-result'),
    providerCapability: versionedIdentifier,
    actionId: versionedIdentifier,
    targetResourceType: identifier,
    targetResourceId: uuid.nullable(),
    outcome: z.enum(['completed', 'unavailable', 'failed']),
    evidence: platformApprovedActionEvidenceSchema.nullable(),
    failure: z.strictObject({
        code: z.enum([
            'input-invalid', 'target-unavailable', 'target-changed',
            'permission-denied', 'action-unavailable', 'action-failed',
        ]),
        message: z.string().trim().min(1).max(500),
        retryable: z.boolean(),
    }).nullable(),
};
function refineApprovedActionResult(result, context) {
    if ((result.outcome === 'completed') !== (result.evidence !== null)) {
        context.addIssue({
            code: 'custom', path: ['evidence'],
            message: 'Completed actions require evidence and non-completed actions must not include it.',
        });
    }
    if ((result.outcome === 'completed') === (result.failure !== null)) {
        context.addIssue({
            code: 'custom', path: ['failure'],
            message: 'Non-completed actions require one failure and completed actions must not include one.',
        });
    }
}
export const platformApprovedActionProviderResultV1Schema = z.strictObject({
    ...platformApprovedActionProviderResultFields,
    contractVersion: z.literal(platformOptionalActionProviderHostCapability),
}).superRefine(refineApprovedActionResult);
export const platformApprovedActionProviderResultV2Schema = z.strictObject({
    ...platformApprovedActionProviderResultFields,
    contractVersion: z.literal(platformReceiptBoundActionProviderHostCapability),
}).superRefine(refineApprovedActionResult);
/** Backward-compatible default for existing v1 provider packages. */
export const platformApprovedActionProviderResultSchema = platformApprovedActionProviderResultV1Schema;
export const platformApprovedActionProviderAnyResultSchema = z.union([
    platformApprovedActionProviderResultV1Schema,
    platformApprovedActionProviderResultV2Schema,
]);
export const platformApprovedActionProviderResponseSchema = z.union([
    platformApprovedActionProviderDescriptorSchema,
    platformApprovedActionProviderAnyResultSchema,
]);
//# sourceMappingURL=platform-optional-action-provider-host.contract.js.map